import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, CheckCircle, Building2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SwitchLocation = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get user data with locations
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = userResponse.data;
      setLocations(userData.locations || []);
      
      // Get current location from localStorage (the source of truth)
      // This is set by both LocationSwitcher and Layout components
      const savedLocationId = localStorage.getItem('currentLocationId');
      const activeLocation = savedLocationId || (userData.locations && userData.locations.length > 0 ? userData.locations[0].id : userData.location_id);
      
      setCurrentLocation(activeLocation);
      console.log('Current location ID:', activeLocation);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLocation = (locationId) => {
    setCurrentLocation(locationId);
    localStorage.setItem('currentLocationId', locationId);
    
    // Redirect to dashboard to refresh with new location
    navigate('/');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading locations...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-1 sm:mb-2" style={{ fontFamily: 'Manrope' }}>Switch Location</h1>
        <p className="text-gray-600">Select a location to view its data and manage leads</p>
      </div>

      {locations.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-600 text-center">No locations available</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map((location) => {
          const isCurrentLocation = location.id === currentLocation;
          
          return (
            <Card 
              key={location.id}
              className={`cursor-pointer transition-all hover:shadow-lg ${
                isCurrentLocation ? 'ring-2 ring-blue-500 bg-blue-50' : ''
              }`}
              onClick={() => handleSelectLocation(location.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                      isCurrentLocation ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {isCurrentLocation ? (
                        <CheckCircle className="w-6 h-6 text-blue-600" />
                      ) : (
                        <Building2 className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{location.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {location.city}, {location.state}
                      </CardDescription>
                    </div>
                  </div>
                  {isCurrentLocation && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                      Current
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {location.address && (
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{location.address}</span>
                  </div>
                )}
                {location.phone && (
                  <div className="text-sm text-gray-600 mt-2">
                    📞 {location.phone}
                  </div>
                )}
                
                {location.subscription_plan && (
                  <div className="mt-3 pt-3 border-t">
                    <span className="text-xs font-medium text-gray-500 uppercase">Plan</span>
                    <p className="text-sm font-semibold text-gray-900 capitalize mt-1">
                      {location.subscription_plan}
                    </p>
                  </div>
                )}
                
                {!isCurrentLocation && (
                  <div className="mt-4 flex items-center justify-end text-blue-600 text-sm font-medium">
                    Switch to this location
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {locations.length > 0 && (
        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> When you switch locations, all data (leads, dashboard metrics, etc.) will be filtered for the selected location. You can switch back anytime from this page.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SwitchLocation;
